import axios from 'axios'
import store from '@/store'
import { Message } from 'element-ui'

// 线上环境为 HTTP；此处即后端 API 根地址（含 /api）。axios 的 baseURL 再拼 /api/admin；上传图片归一化用的公网 origin 也由此解析（与协议、域名保持一致）。
export const API_HOST = 'https://gxc.antissoft.com/api'
export const ADMIN_API_BASE = `${API_HOST}/api/admin`

/** 静态文件由 Flask 提供：GET /api/admin/<path>，打卡图为 /api/admin/checkin/YYYYMM/文件名（与 FILES_BASE_URL 一致，仅一层 /api） */
function uploadFilePublicBase() {
  const fromEnv = (process.env.VUE_APP_FILE_BASE || '').trim().replace(/\/$/, '')
  if (fromEnv) return fromEnv
  return String(API_HOST || '').replace(/\/$/, '')
}

function uploadPublicOrigin() {
  const fromEnv = (process.env.VUE_APP_UPLOAD_ORIGIN || '').trim().replace(/\/$/, '')
  if (fromEnv) return fromEnv
  try {
    const h = (API_HOST || '').trim()
    if (h) return new URL(h).origin
  } catch (e) {
    /* ignore */
  }
  return typeof window !== 'undefined' ? window.location.origin : ''
}

/** 把后端的 /admin/... 或 /api/admin/... 转成浏览器可访问的完整 URL（Flask：/api/admin/checkin/... 或 /api/admin/YYYYMM/...） */
function buildFileUrlFromAdminPath(pathAfterStrip) {
  const base = uploadFilePublicBase()
  let rest = String(pathAfterStrip || '')
  if (!rest || rest === '/') return base
  if (!rest.startsWith('/')) rest = `/${rest}`
  // slice 后 rest 形如 /checkin/202604/x 或 /202604/x → .../api/admin/checkin/... 或 .../api/admin/202604/...
  return `${base.replace(/\/$/, '')}/admin${rest}`
}

function fileUrlFromPathname(pathname) {
  const p = String(pathname || '').trim()
  if (!p.startsWith('/')) return ''
  if (p.startsWith('/api/admin/')) {
    return buildFileUrlFromAdminPath(p.slice('/api/admin'.length))
  }
  if (p.startsWith('/admin/') || p === '/admin') {
    return buildFileUrlFromAdminPath(p.slice('/admin'.length))
  }
  return `${uploadPublicOrigin()}${p}`
}

/** 从任意 http(s) 链接截取路径（含 query）；非法 host 时 new URL 会失败，用正则兜底 */
function pathnameFromHttpUrl(rawUrl) {
  const s = String(rawUrl || '').trim()
  if (!s) return ''
  try {
    const u = new URL(s)
    return `${u.pathname}${u.search || ''}`
  } catch (e) {
    const m = s.match(/^https?:\/\/[^/?#]+(\/[^?#]*)(\?[^#]*)?/i)
    if (m) {
      const path = m[1] && m[1].length ? m[1] : '/'
      return path + (m[2] || '')
    }
    return ''
  }
}

/**
 * 把已保存的图片地址统一成当前环境可访问的 URL（列表/详情里的内网地址、错误 IP 等）
 */
export function normalizeMediaUrl(href) {
  if (href == null) return ''
  if (typeof href === 'object') return resolveUploadedFileUrl(href)
  let s = String(href).trim()
  if (!s) return ''
  if (/^https?:\/\//i.test(s)) {
    // 历史错误：/api/api/admin/...（Flask 无此路由，会 404）→ 纠正为 /api/admin/checkin/...
    try {
      const u = new URL(s)
      if (u.pathname && u.pathname.startsWith('/api/api/admin/')) {
        const tail = u.pathname.slice('/api/api/admin/'.length)
        u.pathname = tail.startsWith('checkin/')
          ? `/api/admin/${tail}`
          : `/api/admin/checkin/${tail}`
        s = u.toString()
      }
    } catch (e) {
      s = String(s)
        .replace(/\/api\/api\/admin\/checkin\//gi, '/api/admin/checkin/')
        .replace(/\/api\/api\/admin\//gi, '/api/admin/checkin/')
    }
    const pathPart = pathnameFromHttpUrl(s)
    if (pathPart) return fileUrlFromPathname(pathPart) || s
    return s
  }
  if (s.startsWith('/')) {
    let p = s
    if (p.startsWith('/api/api/admin/')) {
      const tail = p.slice('/api/api/admin/'.length)
      p = tail.startsWith('checkin/') ? `/api/admin/${tail}` : `/api/admin/checkin/${tail}`
    }
    return fileUrlFromPathname(p) || `${uploadPublicOrigin()}${p}`
  }
  return s
}

/**
 * 上传接口返回的 url 常带后端内网地址，这里换成当前环境可访问的公网地址。
 * 优先用返回的 path/url 中的路径，再拼 VUE_APP_UPLOAD_ORIGIN 或 API_HOST 的 origin。
 */
export function resolveUploadedFileUrl(data) {
  if (!data || typeof data !== 'object') return ''
  const rawUrl = typeof data.url === 'string' ? data.url.trim() : ''
  const path = typeof data.path === 'string' ? data.path.trim() : ''

  if (path.startsWith('/admin/') || path === '/admin' || path.startsWith('/api/admin/')) {
    const u = fileUrlFromPathname(path)
    if (u) return u
  }
  if (rawUrl) {
    const pathPart = pathnameFromHttpUrl(rawUrl)
    if (pathPart) {
      const u = fileUrlFromPathname(pathPart)
      if (u) return u
    }
    if (rawUrl.startsWith('/')) {
      return fileUrlFromPathname(rawUrl) || `${uploadPublicOrigin()}${rawUrl}`
    }
  }
  if (path.startsWith('/')) {
    return fileUrlFromPathname(path) || `${uploadPublicOrigin()}${path}`
  }
  return rawUrl
}

/** 兼容不同接口包装：{ data:{url,path} } 或 { url, path } */
export function pickUploadResponsePayload(res) {
  if (!res || typeof res !== 'object') return {}
  const d = res.data
  if (d && typeof d === 'object' && (d.url != null || d.path != null)) return d
  if (res.url != null || res.path != null) return res
  return d && typeof d === 'object' ? d : {}
}
const service = axios.create({
  baseURL: ADMIN_API_BASE,
  timeout: 60000  // 60 秒，避免登录/慢接口被误判为超时
})

service.interceptors.request.use(
  config => {
    const token = store.state.token || window.localStorage.getItem('admin_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => Promise.reject(error)
)

service.interceptors.response.use(
  response => {
    const payload = response.data
    // 后端统一返回 { code, data, msg/message } 时，code!=200 视为业务失败
    if (payload && typeof payload === 'object' && 'code' in payload && payload.code !== 200) {
      const msg = payload.msg || payload.message || '请求失败'
      Message.error(msg)
      return Promise.reject(payload)
    }
    return payload
  },
  error => {
    // axios 超时场景：error.code = 'ECONNABORTED' / error.message includes 'timeout'
    if (error && (error.code === 'ECONNABORTED' || String(error.message || '').toLowerCase().includes('timeout'))) {
      Message.error('请求超时：请检查后端是否已启动，以及后台地址是否可访问（例如打开 /api/health）')
      return Promise.reject({ code: 408, msg: '请求超时' })
    }
    const payload = error && error.response ? (error.response.data || {}) : (error || {})
    const status = error && error.response ? error.response.status : 0
    const code = payload.code || status
    const msg = payload.msg || payload.message || '请求失败'

    // token 失效/未登录：统一提示并跳转登录
    if (code === 401 || code === 422) {
      try {
        store.commit('SET_TOKEN', '')
      } catch (e) {}
      window.localStorage.removeItem('admin_token')
      window.localStorage.removeItem('admin_username')
      window.localStorage.removeItem('admin_role')
      Message.error(msg)
      if (window.location.hash !== '#/login') {
        window.location.hash = '#/login'
      }
      return Promise.reject(payload)
    }

    Message.error(msg)
    return Promise.reject(payload)
  }
)

export default service

